import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Alert, IconButton, Chip, TextField, InputAdornment, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete, Refresh } from '@mui/icons-material';
import { clientApi } from '../services/api';
import { Client, FilingStatus } from '../types';
import { useNavigate } from 'react-router-dom';
import { Search } from '@mui/icons-material';
import { Select, MenuItem, FormControl, InputLabel, Checkbox, ListItemText } from '@mui/material';

const ClientsList: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem('cl_search') || '');
  const [statusFilter, setStatusFilter] = useState<string[]>(() => { try { const s = localStorage.getItem('cl_status'); return s ? JSON.parse(s) : []; } catch { return []; } });
  const [employeeFilter, setEmployeeFilter] = useState<string[]>(() => { try { const s = localStorage.getItem('cl_employee'); return s ? JSON.parse(s) : []; } catch { return []; } });
  const [clientStatusFilter, setClientStatusFilter] = useState<string>(() => localStorage.getItem('cl_clientStatus') || 'ACTIVE');
  const [frequencyFilter, setFrequencyFilter] = useState<string[]>(() => { try { const s = localStorage.getItem('cl_frequency'); return s ? JSON.parse(s) : []; } catch { return []; } });
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canModify = user.role === 'ADMIN' || user.role === 'SUPER_ADMIN';
  const isEmployee = user.role === 'EMPLOYEE';
  const canEdit = user.role === 'ADMIN' || user.role === 'SUPER_ADMIN';
  const [dynamicColumns, setDynamicColumns] = useState<GridColDef[]>([]);
  useEffect(() => {
    fetchClients();
    
    const params = new URLSearchParams(window.location.search);
    const statusParam = params.get('status');
    const employeeParam = params.get('employee');
    
    if (statusParam) {
      setStatusFilter([statusParam]);
    }
    if (employeeParam) {
      setEmployeeFilter([employeeParam]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters();
  }, [clients, searchTerm, statusFilter, employeeFilter, clientStatusFilter, frequencyFilter]);
  useEffect(() => {
    localStorage.setItem('cl_search', searchTerm);
    localStorage.setItem('cl_status', JSON.stringify(statusFilter));
    localStorage.setItem('cl_employee', JSON.stringify(employeeFilter));
    localStorage.setItem('cl_clientStatus', clientStatusFilter);
    localStorage.setItem('cl_frequency', JSON.stringify(frequencyFilter));
  }, [searchTerm, statusFilter, employeeFilter, clientStatusFilter, frequencyFilter]);
const handleMouseDown = (columnField: string, e: React.MouseEvent) => {
  e.preventDefault();
  const startX = e.clientX;
  let differenceX = 0;

  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';

  const originalColumn = dynamicColumns.find(col => col.field === columnField);
  const originalWidth = originalColumn?.width || 150;

  const handleMouseMove = (e: MouseEvent) => {
    differenceX = e.clientX - startX;
    const newWidth = Math.max(originalWidth + differenceX, 80);
    
    setDynamicColumns(prevColumns => 
      prevColumns.map(col => {
        if (col.field === columnField) {
          return { ...col, width: newWidth };
        }
        return col;
      })
    );
  };

  const handleMouseUp = () => {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
};

useEffect(() => {
  setDynamicColumns(columns);
}, []);

useEffect(() => {
  const handleGlobalMouseDown = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    
    if (target.closest('.MuiDataGrid-columnHeader')) {
      const header = target.closest('.MuiDataGrid-columnHeader');
      if (header) {
        const rect = header.getBoundingClientRect();
        const isRightEdge = e.clientX > rect.right - 8;
        
        if (isRightEdge) {
          const fieldName = header.getAttribute('data-field');
          if (fieldName) {
            handleMouseDown(fieldName, e as any);
          }
        }
      }
    }
  };

  document.addEventListener('mousedown', handleGlobalMouseDown);
  
  return () => {
    document.removeEventListener('mousedown', handleGlobalMouseDown);
  };
}, [dynamicColumns]);
  const fetchClients = async () => {
    try {
      setError(null);
      setLoading(true);
      const response = await clientApi.getAll();
      setClients(response.data.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...clients];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(client =>
        client.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.clientEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.employeeAssigned.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter.length > 0) {
      filtered = filtered.filter(client => statusFilter.includes((client as any).currentFilingStatus));
    }
    // Employee filter
    if (employeeFilter.length > 0) {
      filtered = filtered.filter(client => employeeFilter.includes(client.employeeAssigned));
    }
    // Client status filter
    if (clientStatusFilter !== 'all') {
      if (clientStatusFilter === 'ACTIVE') {
        filtered = filtered.filter(client => client.status !== 'INACTIVE');
      } else if (clientStatusFilter === 'INACTIVE') {
        filtered = filtered.filter(client => client.status === 'INACTIVE');
      }
    }

    // Frequency filter
    if (frequencyFilter.length > 0) {
      filtered = filtered.filter(client => frequencyFilter.includes(client.hstFrequency));
    }

    setFilteredClients(filtered);
  };
  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter([]);
    setEmployeeFilter([]);
    setClientStatusFilter('all');
    setFrequencyFilter([]);
    ['cl_search','cl_status','cl_employee','cl_clientStatus','cl_frequency'].forEach(k => localStorage.removeItem(k));
  };
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await clientApi.delete(id);
        fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
        alert('Failed to delete client');
      }
    }
  };
  const getStatusColor = (status: FilingStatus) => {
    switch (status) {
      case FilingStatus.FILED:
        return { bg: '#D1FAE5', text: '#065F46', border: '#6EE7B7' };
      case FilingStatus.PENDING:
        return { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' };
      case FilingStatus.WORK_IN_PROGRESS:
        return { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD' };
      case FilingStatus.RECEIVED:
        return { bg: '#E0E7FF', text: '#3730A3', border: '#A5B4FC' };
      case FilingStatus.OVERDUE:
        return { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' };
      default:
        return { bg: '#F3F4F6', text: '#374151', border: '#D1D5DB' };
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'clientName',
      headerName: 'Client Name',
      width: 200,
      renderCell: (params) => (
        <Box
          sx={{
            cursor: 'pointer',
            color: '#667eea',
            fontWeight: 600,
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={() => navigate(`/clients/${params.row.id}`)}
        >
          {params.value}
        </Box>
      ),
    },
    { 
      field: 'clientEmail', 
      headerName: 'Client Email', 
      width: 250,
    },
    { 
      field: 'hstFrequency', 
      headerName: 'Frequency', 
      width: 110,
    },
    { 
      field: 'employeeAssigned', 
      headerName: 'Employee', 
      width: 130,
    },
    {
      field: 'clientStatus',
      headerName: 'Client Status',
      width: 120,
      renderCell: (params) => {
        const isInactive = params.row.status === 'INACTIVE';
        return (
          <Chip
            label={isInactive ? 'INACTIVE' : 'ACTIVE'}
            sx={{
              backgroundColor: isInactive ? '#F3F4F6' : '#D1FAE5',
              color: isInactive ? '#6B7280' : '#065F46',
              border: `1px solid ${isInactive ? '#D1D5DB' : '#6EE7B7'}`,
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
            }}
          />
        );
      },
    },
    {
      field: 'currentFilingStatus',
      headerName: 'Filing Status',
      width: 140,
      valueGetter: (params: any) => params.row.currentFilingStatus || params.row.status,
      renderCell: (params) => {
        const status = params.row.currentFilingStatus || params.row.status;
        const colors = getStatusColor(status);
        return (
          <Chip
            label={status}
            sx={{
              backgroundColor: colors.bg,
              color: colors.text,
              border: `1px solid ${colors.border}`,
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
            }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            onClick={() => navigate(`/clients/${params.row.id}`)}
            sx={{
              textTransform: 'none',
              color: '#667eea',
              border: '1px solid #667eea',
              px: 2,
              py: 0.5,
              fontSize: '0.875rem',
              '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.08)' },
            }}
          >
            View
          </Button>
          {canEdit && (
            <IconButton
              size="small"
              onClick={() => navigate(`/clients/edit/${params.row.id}`)}
              sx={{ color: '#667eea' }}
              title="Edit"
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '2rem', mb: 1, color: '#1a1a1a' }}>
            Clients
          </Typography>
          <Typography sx={{ color: '#666666', fontSize: '1rem' }}>
            Manage your client information and filing schedules
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            startIcon={<Refresh />}
            onClick={fetchClients}
            sx={{
              textTransform: 'none',
              color: '#667eea',
              border: '1px solid #E5E7EB',
              fontWeight: 600,
              px: 3,
              py: 1.25,
              '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.08)', borderColor: '#667eea' },
            }}
          >
            Refresh
          </Button>
          {canModify && (
            <Button
              startIcon={<Add />}
              onClick={() => navigate('/clients/new')}
              sx={{
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#FFFFFF',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)' },
              }}
            >
              Add Client
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {/* Stats Cards - ADD THIS */}
        <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2 }}>
        {[
          {
            label: 'Active Clients',
            count: filteredClients.filter(c => c.status !== 'INACTIVE').length,
            color: '#10B981',
            bg: '#D1FAE5',
            onClick: () => {
              setClientStatusFilter('ACTIVE');
              setStatusFilter([]);          
            },
          },
          {
            label: 'Inactive Clients',
            count: filteredClients.filter(c => c.status === 'INACTIVE').length,
            color: '#6B7280',
            bg: '#F3F4F6',
            onClick: () => {
              setClientStatusFilter('INACTIVE');
              setStatusFilter([]);          
            },
          },
          {
            label: 'Pending Clients',
            count: filteredClients.filter(c => (c as any).currentFilingStatus === 'PENDING').length,
            color: '#F59E0B',
            bg: '#FEF3C7',
            onClick: () => {
              setStatusFilter(['PENDING']);
              setClientStatusFilter('ACTIVE');    
            },
          },
          {
            label: 'Overdue Clients',
            count: filteredClients.filter(c => (c as any).currentFilingStatus === 'OVERDUE').length,
            color: '#EF4444',
            bg: '#FEE2E2',
            onClick: () => {
              setStatusFilter(['OVERDUE']);
              setClientStatusFilter('ACTIVE');    
            },
          },
        ].map((stat) => (
          <Box
            key={stat.label}
            onClick={stat.onClick}
            sx={{
              p: 3,
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { borderColor: stat.color, transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
            }}
          >
            <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: 600, mb: 1 }}>{stat.label}</Typography>
            <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: stat.color }}>{stat.count}</Typography>
          </Box>
        ))}
      </Box>
      {/* Search and Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search Bar */}
        <TextField
          placeholder="Search clients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            flex: 1,
            minWidth: 300,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '& fieldset': { borderColor: '#E5E7EB' },
              '&:hover fieldset': { borderColor: '#667eea' },
              '&.Mui-focused fieldset': { borderColor: '#667eea' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#6B7280' }} />
              </InputAdornment>
            ),
          }}
        />
        {/* Status Filter */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filing Status</InputLabel>
          <Select
            multiple
            value={statusFilter}
            label="Filing Status"
            onChange={(e) => { const val = e.target.value as string[]; if (val.includes('all')) { setStatusFilter([]); } else { setStatusFilter(val); } }}
            renderValue={() => statusFilter.length === 0 ? 'All Statuses' : `${statusFilter.length} selected`}
            sx={{
              backgroundColor: '#FFFFFF',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
            }}
          >
            <MenuItem value="all"><Checkbox checked={statusFilter.length === 0} /><ListItemText primary="All Statuses" /></MenuItem>
            <MenuItem value="PENDING"><Checkbox checked={statusFilter.includes('PENDING')} /><ListItemText primary="Pending" /></MenuItem>
            <MenuItem value="WORK_IN_PROGRESS"><Checkbox checked={statusFilter.includes('WORK_IN_PROGRESS')} /><ListItemText primary="Work In Progress" /></MenuItem>
            <MenuItem value="RECEIVED"><Checkbox checked={statusFilter.includes('RECEIVED')} /><ListItemText primary="Received" /></MenuItem>
            <MenuItem value="FILED"><Checkbox checked={statusFilter.includes('FILED')} /><ListItemText primary="Filed" /></MenuItem>
            <MenuItem value="OVERDUE"><Checkbox checked={statusFilter.includes('OVERDUE')} /><ListItemText primary="Overdue" /></MenuItem>
          </Select>
          {statusFilter.length > 0 && (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>{statusFilter.map(s => (<Chip key={s} label={s.replace(/_/g, ' ')} size="small" onDelete={() => setStatusFilter(statusFilter.filter(x => x !== s))} sx={{ fontSize: '0.7rem', height: 22, backgroundColor: '#EEF2FF', color: '#667eea', fontWeight: 600 }} />))}</Box>)}
        </FormControl>
  
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Employee</InputLabel>
          <Select
            multiple
            value={employeeFilter}
            label="Employee"
            onChange={(e) => { const val = e.target.value as string[]; if (val.includes('all')) { setEmployeeFilter([]); } else { setEmployeeFilter(val); } }}
            renderValue={() => employeeFilter.length === 0 ? 'All Employees' : `${employeeFilter.length} selected`}
            sx={{
              backgroundColor: '#FFFFFF',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
            }}
          >
            <MenuItem value="all"><Checkbox checked={employeeFilter.length === 0} /><ListItemText primary="All Employees" /></MenuItem>
            {Array.from(new Set(clients.map(cl => cl.employeeAssigned))).map((emp) => (<MenuItem key={emp} value={emp}><Checkbox checked={employeeFilter.includes(emp)} /><ListItemText primary={emp} /></MenuItem>))}
          </Select>
          {employeeFilter.length > 0 && (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>{employeeFilter.map(e => (<Chip key={e} label={e} size="small" onDelete={() => setEmployeeFilter(employeeFilter.filter(x => x !== e))} sx={{ fontSize: '0.7rem', height: 22, backgroundColor: '#EEF2FF', color: '#667eea', fontWeight: 600 }} />))}</Box>)}
          </FormControl>

          {/* Client Status Filter */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Client Status</InputLabel>
            <Select
              value={clientStatusFilter}
              label="Client Status"
              onChange={(e) => setClientStatusFilter(e.target.value)}
              sx={{
                backgroundColor: '#FFFFFF',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
              }}
            >
              <MenuItem value="all">All Clients</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </Select>
          </FormControl>

          {/* Frequency Filter */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Frequency</InputLabel>
            <Select
              multiple
              value={frequencyFilter}
              label="Frequency"
              onChange={(e) => { const val = e.target.value as string[]; if (val.includes('all')) { setFrequencyFilter([]); } else { setFrequencyFilter(val); } }}
              renderValue={() => frequencyFilter.length === 0 ? 'All Frequencies' : `${frequencyFilter.length} selected`}
              sx={{
                backgroundColor: '#FFFFFF',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
              }}
            >
              <MenuItem value="all"><Checkbox checked={frequencyFilter.length === 0} /><ListItemText primary="All Frequencies" /></MenuItem>
              <MenuItem value="MONTHLY"><Checkbox checked={frequencyFilter.includes('MONTHLY')} /><ListItemText primary="Monthly" /></MenuItem>
              <MenuItem value="QUARTERLY"><Checkbox checked={frequencyFilter.includes('QUARTERLY')} /><ListItemText primary="Quarterly" /></MenuItem>
              <MenuItem value="ANNUAL"><Checkbox checked={frequencyFilter.includes('ANNUAL')} /><ListItemText primary="Annual" /></MenuItem>
            </Select>
            {frequencyFilter.length > 0 && (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>{frequencyFilter.map(f => (<Chip key={f} label={f.charAt(0) + f.slice(1).toLowerCase()} size="small" onDelete={() => setFrequencyFilter(frequencyFilter.filter(x => x !== f))} sx={{ fontSize: '0.7rem', height: 22, backgroundColor: '#EEF2FF', color: '#667eea', fontWeight: 600 }} />))}</Box>)}
          </FormControl>

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
            {(searchTerm || statusFilter.length > 0 || employeeFilter.length > 0 || clientStatusFilter !== 'all' || frequencyFilter.length > 0) && (
              <Button
                onClick={clearAllFilters}
                sx={{
                  textTransform: 'none',
                  color: '#EF4444',
                  border: '1px solid #EF4444',
                  fontWeight: 600,
                  px: 2,
                  py: 0.75,
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: '#FEF2F2',
                    borderColor: '#DC2626',
                  },
                }}
              >
                Clear All Filters
              </Button>
            )}
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              Showing {filteredClients.length} of {clients.length} clients
            </Typography>
          </Box>
      </Box>

      {/* Data Grid */}
      <Box
        sx={{
          width: '100%',
          height: 600,
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          overflow: 'hidden',
        }}
      >
        <DataGrid
          rows={filteredClients}
          columns={dynamicColumns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': { borderBottom: '1px solid #F3F4F6', fontSize: '0.9375rem', color: '#374151' },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#F9FAFB',
              borderBottom: '1px solid #E5E7EB',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#6B7280',
            },
    '& .MuiDataGrid-columnHeader': {
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        cursor: 'col-resize',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: '#667eea',
        },
      },
    },
            '& .MuiDataGrid-row:hover': { backgroundColor: '#F9FAFB' },
            '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' },
          }}
        />
      </Box>
    </Box>
  );
};

export default ClientsList;
